import {
  formatDateToMMDDYYYY,
  RoutineWorkflow,
  useCustomQuery,
  useDeleteMutation,
  useGetQuery,
  useRoutineAlerts,
  useUpdateMutation,
  useWorkspaceRoutine,
  WorkspaceRoutine,
} from '@gripp/shared-logic';
import { MutableRefObject, useEffect, useMemo, useState } from 'react';
import LoadingIndicator from '../../loadingIndicator';
import { AlertForm } from './alertForm';
import { excludedConfigKeys } from './alertSummary';
import { GroupAlertAssetForm } from './groupAlertAssetForm';
import { getAlertType, getCounterRoutinesForOnInterval } from './helpers';
import { AlertFormRef, AlertType } from './types';

/** The entity an alert hangs off, per GET_WORKFLOW. */
type WorkflowOwner = {
  workspaceAsset?: { id?: string } | null;
  product?: { id?: string } | null;
  group?: { id?: string } | null;
  trigger?: { config?: { workspaceRoutineId?: string } } | null;
};

type EditAlertFormProps = {
  onClose: () => void;
  alertId: string;
  /**
   * The routine the alert prompts. Callers that navigated in through a routine
   * pass it; entity-level entry points (a group's alert list) know only the
   * alert, and it is read off the trigger config once the workflow loads.
   */
  routineId?: string;
  workspaceAssetId?: string;
  productId?: string;
  groupId?: string;
  formRef?: MutableRefObject<AlertFormRef>;
  renderBottomComponent?: (onSubmit: () => void, disabled: boolean) => React.ReactNode;
  onRegisterBackGuard?: (guard: (onProceed: () => void, onCancel?: () => void) => void) => void;
};

export const EditAlertForm = ({
  onClose,
  alertId,
  routineId,
  workspaceAssetId,
  productId,
  groupId,
  formRef,
  renderBottomComponent,
  onRegisterBackGuard,
}: EditAlertFormProps) => {
  const { mutateAsync: updateWorkflow } = useUpdateMutation({
    modelName: 'workflow',
    query: UPDATE_WORKFLOW,
  });

  const { mutateAsync: deleteWorkflow } = useDeleteMutation({
    modelName: 'workflow',
    query: DELETE_WORKFLOW,
  });

  const { data: workflowData, isLoading: isWorkflowLoading } = useGetQuery({
    modelName: 'workflow',
    query: GET_WORKFLOW,
    id: alertId,
  });

  // Callers that know the owning entity pass it; the rest (the calendar's alert
  // pill knows only routine + alert) get it from the workflow, which
  // GET_WORKFLOW already returns. The owner is what resolves counter routines.
  const owner = workflowData as WorkflowOwner | undefined;
  const ownerAssetId = workspaceAssetId || owner?.workspaceAsset?.id;
  const ownerProductId = productId || owner?.product?.id;
  const ownerGroupId = groupId || owner?.group?.id;
  /**
   * A group-owned alert opened from one of its member assets. The trigger is
   * the group's, so this view is read-only apart from the asset's own interval
   * anchor - see GroupAlertAssetForm. The group's own alert list passes no
   * asset and gets the full editor.
   */
  const isGroupAlertAtAssetLevel = !!ownerGroupId && !!workspaceAssetId;

  const effectiveRoutineId =
    routineId || owner?.trigger?.config?.workspaceRoutineId || '';

  const { data: groupAssetsData } = useCustomQuery({
    query: GET_GROUP_ASSETS,
    cacheKey: ['group-assets-edit', ownerGroupId],
    variables: { assetFilter: { groups: { eq: ownerGroupId } } },
    options: { enabled: !!ownerGroupId && !isGroupAlertAtAssetLevel },
  });

  const groupAssets = ownerGroupId
    ? (groupAssetsData as any)?.findWorkspaceAssets?.items ?? []
    : undefined;

  const routineData = useWorkspaceRoutine(effectiveRoutineId);

  const { workspaceAsset, isLoading: isAssetLoading } = useRoutineAlerts(
    ownerAssetId || '',
    effectiveRoutineId,
    ownerProductId
  );

  const isLoading = isWorkflowLoading || isAssetLoading;

  const [workflow, setWorkflow] = useState<RoutineWorkflow>();
  const [defaultObject, setDefaultObject] = useState<any>(null);

  // Derived, not latched alongside the defaults below: with the owner coming
  // from the workflow the asset lands a beat later, and a copy taken at latch
  // time would hold the earlier empty array.
  const counterRoutines: WorkspaceRoutine[] = useMemo(
    () =>
      workspaceAsset ? getCounterRoutinesForOnInterval(workspaceAsset) : [],
    [workspaceAsset]
  );

  useEffect(() => {
    setWorkflow(undefined);
    setDefaultObject(null);
  }, [alertId]);

  useEffect(() => {
    if (!workflow && !isLoading) {
      const routineWorkflow = workflowData as RoutineWorkflow;

      if (!routineWorkflow?.trigger?.config) {
        return;
      }

      const configKeys = Object.keys(routineWorkflow.trigger.config);

      const alertKeys = configKeys.filter(
        (key) => !excludedConfigKeys.includes(key)
      );
      const alertType = getAlertType(alertKeys[0]);
      const secondAlertType =
        (alertKeys.length > 1 && getAlertType(alertKeys[1])) || undefined;
      const minValue = routineWorkflow?.trigger?.config?.ceiling;
      const maxValue = routineWorkflow?.trigger?.config?.floor;
      const dayInterval = routineWorkflow?.trigger?.config?.dayInterval;
      const dayIntervalUnit = routineWorkflow?.trigger?.config?.dayIntervalUnit;
      const startDateValue = formatDateToMMDDYYYY(
        routineWorkflow?.trigger?.config?.startDate
      );
      const completionsValue =
        routineWorkflow?.trigger?.config?.onCompletionCount;
      const daysSkippedValue =
        routineWorkflow?.trigger?.config?.daysSinceCompleted;
      const onDateValue = formatDateToMMDDYYYY(
        routineWorkflow?.trigger?.config?.onDate
      );
      const onIntervalValue = routineWorkflow?.trigger?.config?.onInterval;
      const onIntervalStartValue =
        routineWorkflow?.trigger?.config?.onIntervalStart;
      const onIntervalCounterId =
        routineWorkflow?.trigger?.config?.triggerCounterRoutineId;
      const startDate = formatDateToMMDDYYYY(
        routineWorkflow?.disabledBetween?.start
      );
      const endDate = formatDateToMMDDYYYY(
        routineWorkflow?.disabledBetween?.end
      );

      const getInputValue = (alertType: AlertType | undefined) => {
        switch (alertType) {
          case AlertType.DaysSkipped:
            return daysSkippedValue;

          case AlertType.RoutineCompletions:
            return completionsValue;

          case AlertType.Min:
            return minValue;

          case AlertType.Max:
            return maxValue;

          default:
            return undefined;
        }
      };

      const defaultValues = {
        alertLabel: routineWorkflow?.name || '',
        alertLabelPlaceholder: routineWorkflow?.name || '',
        startDate: startDate,
        endDate: endDate,
        alertType: alertType,
        secondAlertType: secondAlertType,
        inputField: getInputValue(alertType),
        secondInputField: getInputValue(secondAlertType),
        minValue: minValue,
        maxValue: maxValue,
        completions: completionsValue?.toString(),
        daysSkipped: daysSkippedValue?.toString(),
        onInterval: onIntervalValue?.toString(),
        onIntervalStart: onIntervalStartValue?.toString(),
        intervalRoutineId: onIntervalCounterId,
        onDate: onDateValue,
        dayInterval,
        dayIntervalUnit,
        daysStartDate: startDateValue,
        atTime: routineWorkflow?.trigger?.config?.atTime,
        timeZone: routineWorkflow?.trigger?.config?.timeZone,
      };

      setDefaultObject(defaultValues);
      setWorkflow(routineWorkflow);
    }
  }, [workflowData, isLoading, workflow, workspaceAsset, alertId]);

  const deleteAlert = async () => {
    if (!workflow) return;

    const input = {
      id: workflow.id,
    };

    await deleteWorkflow(
      { input },
      {
        onSettled: () => {
          onClose();
        },
      }
    );
  };

  const submitAlert = async (input: any) => {
    await updateWorkflow(
      { input },
      {
        onSuccess: async () => {
          onClose();
        },
      }
    );
  };

  if (isGroupAlertAtAssetLevel) {
    // The query result, not the `workflow` state below: that one is latched
    // once so the full editor's defaults stay put, and this view has to follow
    // the anchor its own Reset writes.
    const liveWorkflow = workflowData as RoutineWorkflow | undefined;

    return isLoading || !liveWorkflow?.trigger?.config || !workspaceAsset ? (
      <LoadingIndicator />
    ) : (
      <GroupAlertAssetForm
        workflow={liveWorkflow}
        workspaceAsset={workspaceAsset}
        routineId={effectiveRoutineId}
        onClose={onClose}
        formRef={formRef}
        renderBottomComponent={renderBottomComponent}
        onRegisterBackGuard={onRegisterBackGuard}
      />
    );
  }

  return isLoading || !defaultObject || !counterRoutines || !workflow ? (
    <LoadingIndicator />
  ) : (
    <AlertForm
      defaultValues={defaultObject}
      routine={routineData}
      workspaceAsset={workspaceAsset}
      product={ownerProductId ? { id: ownerProductId } : undefined}
      group={ownerGroupId ? { id: ownerGroupId } : undefined}
      groupAssets={groupAssets}
      workflowProps={workflow}
      isEdit
      submitAlert={submitAlert}
      deleteAlert={deleteAlert}
      counterRoutines={counterRoutines}
      onClose={onClose}
      headerTitle="alerts.editAlert"
      formRef={formRef}
      renderBottomComponent={renderBottomComponent}
      onRegisterBackGuard={onRegisterBackGuard}
    />
  );
};

const GET_WORKFLOW = `
  query GetWorkflow($id: ID!) {
    getWorkflow(id: $id) {
      id
      name
      group {
        id
        name
      }
      product {
        id
      }
      workspaceAsset {
        id
      }
      trigger {
        type
        config
      }
      action {
        type
        config
      }
      disabled
      disabledBetween {
        start
        end
      }
      workspace {
        id
      }
    }
  }
`;

const UPDATE_WORKFLOW = `
  mutation UpdateWorkflow($input: MutateWorkflowInput!) {
  updateWorkflow(input: $input) {
    id
  }
}
`;

const DELETE_WORKFLOW = `
  mutation DeleteWorkflow($input: MutateWorkflowInput!) {
    deleteWorkflow(input: $input) {
      id
    }
  }
`;

const GET_GROUP_ASSETS = `
  query GetGroupAssets($assetFilter: WorkspaceAssetFilter!) {
    findWorkspaceAssets(filter: $assetFilter) {
      items {
        id
        asset {
          id
          name
        }
        routineState {
          workspaceRoutineId
          state
        }
      }
    }
  }
`;
